import React, { useState, useCallback, useRef } from 'react';
import MobileBottomSheet from './MobileBottomSheet';

const PIXABAY_API_KEY = '33745979-6e793453ab99977fccede77c0';

export default function MobileImagesSheet({ isOpen, onClose, canvasManager }) {
  const [tab, setTab] = useState('upload');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !canvasManager) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const canvasW = canvasManager.vWidth || 3780;
      canvasManager.addImage(ev.target.result, { maxWidth: Math.round(canvasW * 0.4), name: file.name });
      onClose();
    };
    reader.readAsDataURL(file);
    // Reset so same file can be re-uploaded
    e.target.value = '';
  }, [canvasManager, onClose]);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const q = encodeURIComponent(query.trim());
      const url = `https://pixabay.com/api/?key=${PIXABAY_API_KEY}&q=${q}&image_type=photo&per_page=21&page=1&lang=de`;
      const res = await fetch(url);
      const data = await res.json();
      setResults(data.hits || []);
    } catch (err) {
      console.error('Pixabay search error:', err);
    } finally {
      setLoading(false);
    }
  }, [query]);

  const handleSelectImage = useCallback((img) => {
    if (!canvasManager) return;
    const canvasW = canvasManager.vWidth || 3780;
    canvasManager.addImage(img.largeImageURL || img.webformatURL, {
      maxWidth: Math.round(canvasW * 0.4),
      crossOrigin: 'anonymous',
    });
    onClose();
  }, [canvasManager, onClose]);

  const tabs = [
    { id: 'upload', label: 'Upload' },
    { id: 'search', label: 'Pixabay' },
  ];

  return (
    <MobileBottomSheet isOpen={isOpen} onClose={onClose} title="Bilder" height="45vh">
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', background: 'var(--selenny-bg)', borderRadius: '8px', padding: '3px' }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: 1, padding: '8px', border: 'none', borderRadius: '6px', fontSize: '13px',
              fontWeight: tab === t.id ? 600 : 400, cursor: 'pointer',
              background: tab === t.id ? 'var(--selenny-surface)' : 'transparent',
              color: 'var(--selenny-text)',
              boxShadow: tab === t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Upload tab */}
      {tab === 'upload' && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ display: 'none' }} />
          <button
            onClick={() => fileRef.current?.click()}
            style={{
              padding: '14px 28px', background: 'var(--selenny-primary)', color: '#fff',
              border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 500, cursor: 'pointer',
            }}
          >
            Bild auswählen
          </button>
          <p style={{ marginTop: '8px', fontSize: '12px', opacity: 0.5, color: 'var(--selenny-text)' }}>JPG, PNG, SVG</p>
        </div>
      )}

      {/* Pixabay search tab */}
      {tab === 'search' && (
        <>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Bilder suchen..."
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              style={{
                flex: 1, padding: '10px 12px', border: '1px solid var(--selenny-border)',
                borderRadius: '8px', fontSize: '16px', background: 'var(--selenny-surface)',
                color: 'var(--selenny-text)', outline: 'none',
                minWidth: 0,
              }}
            />
            <button
              onClick={handleSearch}
              style={{
                padding: '10px 14px', background: 'var(--selenny-primary)', color: '#fff',
                border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
                flexShrink: 0, whiteSpace: 'nowrap',
              }}
            >
              Suchen
            </button>
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '20px', opacity: 0.5, color: 'var(--selenny-text)' }}>Suche...</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
            {results.map((img, i) => (
              <button
                key={img.id || i}
                onClick={() => handleSelectImage(img)}
                style={{
                  padding: 0, border: '1px solid var(--selenny-border)', borderRadius: '8px',
                  overflow: 'hidden', cursor: 'pointer', background: 'var(--selenny-bg)',
                  aspectRatio: '1', WebkitTapHighlightColor: 'transparent',
                }}
              >
                <img
                  src={img.previewURL || img.webformatURL}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  loading="lazy"
                />
              </button>
            ))}
          </div>

          {results.length > 0 && (
            <div style={{ textAlign: 'center', padding: '8px 0 4px', fontSize: '10px', opacity: 0.4, color: 'var(--selenny-text)' }}>
              Bilder von Pixabay
            </div>
          )}
        </>
      )}
    </MobileBottomSheet>
  );
}
